import {
    AreaPlot,
    ChartsAxisHighlight,
    ChartsGrid,
    ChartsTooltip,
    ChartsXAxis,
    ChartsYAxis,
    LineChartProps,
    LinePlot,
    ResponsiveChartContainer,
} from "@mui/x-charts";
import { ChartsLoadingOverlay } from "@mui/x-charts/ChartsOverlay";
import { sum } from "lodash";

import { ConsumptionChartTooltip } from "./components/ConsumptionChartTooltip";
import { ConsumptionProjectedArea } from "./components/ConsumptionProjectedArea";
import { BenchmarkSeries, ConsumptionChartData } from "./consumption.types";
import { ChartLegend } from "../views/ReductionAnalysisView/components";
import { ChartLegendSeries } from "../views/ReductionAnalysisView/components/ChartLegend";

type Props = {
    loading: boolean;
    chartData: ConsumptionChartData[];
    filteredSeries: LineChartProps["series"];
    filteredBenchmarks: BenchmarkSeries[];
};

export const ConsumptionChart = (props: Props) => {
    const { loading, chartData, filteredSeries, filteredBenchmarks } = props;

    const series = [...filteredSeries, ...filteredBenchmarks].map((s) => ({
        ...s,
        type: "line",
    }));

    const projectedLimit = chartData.findIndex(
        (d) => d.period === new Date().getFullYear().toString()
    );
    const projectedArea = chartData.slice(projectedLimit).map((d) => ({
        y: sum(Object.values(d).filter((v) => typeof v === "number")) || 0,
    }));

    return (
        <>
            <ResponsiveChartContainer
                height={600}
                dataset={chartData}
                series={filteredSeries.length ? (series as any) : []}
                xAxis={[
                    {
                        id: "Period",
                        dataKey: "period",
                        scaleType: "point",
                        valueFormatter: (year) =>
                            new Date(year, 0, 1).getFullYear().toString(),
                    },
                ]}
                yAxis={[
                    {
                        id: "Consumption",
                        dataKey: "consumption",
                        scaleType: "linear",
                        tickNumber: 5,
                        valueFormatter: (value) =>
                            value > 1000 ? value / 1000 : value,
                    },
                ]}
                sx={(theme) => ({
                    "& path[class*='MuiLineElement-series-consumption']": {
                        strokeWidth: 0,
                    },
                    "& .MuiChartsAxisHighlight-root": {
                        stroke: "rgba(55, 55, 55, 0.1)",
                        strokeWidth: "3.5%",
                        strokeDasharray: "0",
                    },

                    /** Axis style **/
                    "& .MuiChartsAxis-tickLabel": {
                        fill: theme.palette.text.secondary,
                    },
                    "& .MuiChartsAxis-directionY": {
                        transform: "translate(40px, 0)",
                    },
                    "& .MuiChartsAxis-directionX": {
                        transform: "translate(0, 560px)",
                    },
                })}
            >
                {loading && <ChartsLoadingOverlay />}
                <ChartsGrid horizontal />
                <AreaPlot />
                <LinePlot />
                <ChartsXAxis
                    slots={{ axisTick: () => null, axisLine: () => null }}
                />
                <ChartsYAxis
                    slots={{ axisTick: () => null, axisLine: () => null }}
                />
                <ChartsTooltip
                    slots={{ axisContent: ConsumptionChartTooltip }}
                />
                <ConsumptionProjectedArea
                    limit={projectedLimit}
                    forecast={projectedArea}
                />

                <ChartsAxisHighlight x="line" />
            </ResponsiveChartContainer>
            {filteredSeries.length > 0 && (
                <>
                    <ChartLegend
                        series={filteredSeries as ChartLegendSeries[]}
                    />
                    <ChartLegend
                        series={filteredBenchmarks as ChartLegendSeries[]}
                    />
                </>
            )}
        </>
    );
};
